import axios from 'axios'
import { setupCache } from 'axios-cache-adapter'
import config from '@app/config'

import * as AuthStore from '@store/auth/auth'
import * as UserStore from '@store/user/user'
import { store as i18n } from '@i18n/i18n'

// cache and queue
const INTERVAL_MS = 20
const MAX_AGE = 3 * 1000
const cache = setupCache({ maxAge: MAX_AGE, exclude: { query: false } })

const MAX_TIMEOUT = 180000

// refresh
let PENDING = false

class ApiError extends Error {
  code: number

  constructor(message: string, code: number) {
    super(message)
    this.code = code
  }
}

// queue store api calls to prevent parrallelized calls for same api
const QUEUE: Record<string, number> = {}

/**
 * api call entry point with enqueue system to prevent async between refresh after error and api call
 * @param path api path
 * @param method api method
 * @param body api body if exist
 * @returns T the call result or throw error
 */
const executeWithQueue = <T>(
  path: string,
  method: 'GET' | 'PUT' | 'POST' | 'DELETE' | 'PATCH',
  body?: any
): Promise<T> => {
  const key = `${path}_${method}`
  const prev = QUEUE[key]
  const now = Date.now()

  // still in progress, will retry
  if (prev && prev > now) {
    return new Promise((res, rej) =>
      // delay call for 20ms
      setTimeout(() => executeWithQueue<T>(path, method, body).then(res).catch(rej), INTERVAL_MS)
    )
  }

  // no queue, store current and start request
  QUEUE[key] = now + MAX_AGE

  return execute<T>(path, method, body).finally(() => {
    // when over free his place for another request
    QUEUE[key] = 0
  })
}

/**
 * api call main execution
 * @param path api path
 * @param method api method
 * @param body api body if exist
 * @returns T the call result or throw error
 */
const execute = <T>(path: string, method: 'GET' | 'PUT' | 'POST' | 'DELETE' | 'PATCH', body?: any): Promise<T> => {
  const { tokens } = AuthStore.store.getState()

  // headers
  const headers: { [key: string]: string } = {
    'content-type': 'application/json',
    'Accept-Language': i18n.getState().lang,
    Authorization: `Bearer ${tokens ? tokens.access : ''}`,
  }

  // cancel Token used for Timeout
  const cancelToken = axios.CancelToken.source()
  setTimeout(cancelToken.cancel, MAX_TIMEOUT)

  return axios
    .request<T>({
      cancelToken: cancelToken.token,
      url: config.SERVER_PREFIX + path,
      method: method || (body ? 'POST' : 'GET'),
      headers,
      data: body,
      timeout: MAX_TIMEOUT,
      adapter: cache.adapter,
    })
    .then((res) => res.data)
    .catch((err) => {
      if (!err || !err.response) {
        throw err
      }

      const { status } = err.response

      if (status === 401) {
        return retryOrThrow<T>(path, method, body, tokens).catch(() => {
          // Authentication or right error and max retry reached
          throw new ApiError('Expired session ?', status)
        })
      }

      if (status >= 400) {
        // Query or Server Error
        throw new ApiError('Unable to query ' + path, status)
      }

      throw err
    })
    .catch((err: ApiError) => {
      // still on error after refresh, disconnect user and reset tokens
      if (err.code === 401) {
        UserStore.actions.resetUser()
        AuthStore.actions.reset()
      }

      throw err
    })
}

/**
 * Refresh tokens and retry api call after an auth error or break cycle if max error reached
 * @param path api path
 * @param method api method
 * @param body api body if exist
 * @param tokens tokens to refresh
 * @returns api call result or throw error
 */
const retryOrThrow = <T>(
  path: string,
  method: 'GET' | 'PUT' | 'POST' | 'DELETE' | 'PATCH',
  body?: any,
  tokens?: AuthTokens
): Promise<T> => {
  if (!tokens) {
    return Promise.reject('no tokens provided')
  }

  if (!PENDING) {
    PENDING = true

    // will try refresh tokens then execute
    return executeWithQueue<LoginRefreshSuccessResponse>(config.ws.authentification.refresh, 'PUT', {
      refresh_token: tokens.refresh,
    })
      .then((refreshed) => {
        // save new tokens before retry call
        AuthStore.actions.setTokens({
          access: refreshed.access_token,
          expireIn: refreshed.expires_in,
          scopes: refreshed.scopes,
          refresh: tokens.refresh,
        })
      })
      .finally(() => {
        PENDING = false
      })
      .then(() => execute(path, method, body))
  } else {
    return new Promise((res) => setTimeout(() => res(execute(path, method, body)), 300))
  }
}

export default executeWithQueue
